import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { useRbac } from "@/hooks/useRbac";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import {
  Eye, Share2, LayoutGrid, List, Calendar, FileText, MessageSquare,
  Plus, FilePlus2
} from "lucide-react";
import StudyDiscussion from "@/components/StudyDiscussion";
import StudyReportsList from "@/components/StudyReportsList";
import ImportReportDialog from "@/components/ImportReportDialog";
import ShareStudyDialog from "@/components/ShareStudyDialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useModalities } from "@/contexts/ModalitiesContext";
import { getModalityBadgeClass } from "@/lib/modalities";
import { isEchoFormModality, isFormOnlyStudy } from "@/lib/echoMeasurements";
import ModalityDotLabel from "@/components/ModalityDotLabel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SCAN_THUMBS = [
  "https://images.unsplash.com/photo-1516549655169-df83a0774514?auto=format&fit=crop&q=80&w=300",
  "https://images.unsplash.com/photo-1530497610245-94d3c16cda28?auto=format&fit=crop&q=80&w=300",
  "https://images.unsplash.com/photo-1579154204601-01588f351e67?auto=format&fit=crop&q=80&w=300",
  "https://images.unsplash.com/photo-1559757175-0eb30cd8c063?auto=format&fit=crop&q=80&w=300",
];

export default function Studies() {
  const navigate = useNavigate();
  const location = useLocation();
  const { canUpload, canShare, canViewStudies } = useRbac();
  const { modalities, getModality } = useModalities();
  const [studies, setStudies] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("table");
  const [filters, setFilters] = useState({ modality: "", status: "", dataset_type: "" });
  const [discussionStudy, setDiscussionStudy] = useState(null);
  const [importStudy, setImportStudy] = useState(null);
  const [shareStudy, setShareStudy] = useState(null);
  const fetchStudies = useCallback(async () => {
    try {
      const params = {};
      if (filters.modality) params.modality = filters.modality;
      if (filters.status) params.status = filters.status;
      if (filters.dataset_type) params.dataset_type = filters.dataset_type;
      const res = await axios.get(`${API}/studies`, { params });
      setStudies(res.data.studies);
      setTotal(res.data.total);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [filters]);

  useEffect(() => { fetchStudies(); }, [fetchStudies]);

  const clearFilters = () => setFilters({ modality: "", status: "", dataset_type: "" });

  return (
    <div className="space-y-4" data-testid="studies-page">
      {/* Filters + Upload button */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={filters.modality} onValueChange={(v) => setFilters({ ...filters, modality: v === "all" ? "" : v })}>
            <SelectTrigger data-testid="filter-modality" className="w-36 h-9"><SelectValue placeholder="Modality" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Modalities</SelectItem>
              {modalities.map((m) => (
                <SelectItem key={m.key} value={m.key} textValue={m.label}>
                  <ModalityDotLabel modalityKey={m.key} label={m.label} />
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v === "all" ? "" : v })}>
            <SelectTrigger data-testid="filter-status" className="w-32 h-9"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filters.dataset_type} onValueChange={(v) => setFilters({ ...filters, dataset_type: v === "all" ? "" : v })}>
            <SelectTrigger data-testid="filter-dataset" className="w-32 h-9"><SelectValue placeholder="Dataset" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Datasets</SelectItem>
              <SelectItem value="clinical">Clinical</SelectItem>
              <SelectItem value="research">Research</SelectItem>
              <SelectItem value="vendor">Vendor</SelectItem>
            </SelectContent>
          </Select>
          {(filters.modality || filters.status || filters.dataset_type) && (
            <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="clear-filters" className="text-xs">Clear</Button>
          )}
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-muted-foreground">{total} studies</span>
          <div className="flex border border-border rounded-md overflow-hidden">
            <button data-testid="view-grid" onClick={() => setView("grid")}
              className={`p-1.5 ${view === "grid" ? "bg-secondary text-foreground" : "text-muted-foreground hover:text-foreground"}`}>
              <LayoutGrid className="w-4 h-4" />
            </button>
            <button data-testid="view-table" onClick={() => setView("table")}
              className={`p-1.5 ${view === "table" ? "bg-secondary text-foreground" : "text-muted-foreground hover:text-foreground"}`}>
              <List className="w-4 h-4" />
            </button>
          </div>
          {canUpload && (
            <Button
              data-testid="upload-study-btn"
              onClick={() => navigate("/upload", {
                state: { from: `${location.pathname}${location.search}` },
              })}
              size="sm"
              className="gap-1.5 rounded-[10px] shadow-[0_5px_14px_var(--xl-primary-shadow)]"
            >
              <Plus className="w-3.5 h-3.5" /> Upload Study
            </Button>
          )}
        </div>
      </div>

      {view === "grid" ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {loading ? (
            <div className="col-span-full text-center py-12 text-muted-foreground">Loading...</div>
          ) : studies.map((s, i) => (
            <Card key={s.id} className="card-hover overflow-hidden" data-testid={`study-card-${i}`}>
              <div
                className={`aspect-video bg-secondary/50 relative overflow-hidden ${canViewStudies ? "cursor-pointer" : ""}`}
                onClick={() => {
                  if (canViewStudies) navigate(`/viewer/${s.id}`);
                }}
              >
                <img src={SCAN_THUMBS[i % SCAN_THUMBS.length]} alt={s.description}
                     className="w-full h-full object-cover opacity-60" />
                <div className="absolute top-2 left-2 flex flex-wrap items-center gap-1 max-w-[calc(100%-1rem)]">
                  <Badge className={`text-[10px] ${getModalityBadgeClass(s.modality)}`}>
                    {getModality(s.modality)?.label ?? s.modality}
                  </Badge>
                  {isFormOnlyStudy(s) && isEchoFormModality(s.modality) && (
                    <Badge variant="outline" className="text-[10px] font-normal bg-background">Form</Badge>
                  )}
                  {s.mismatched === true && (
                    <Badge className="text-[10px] font-normal border-transparent bg-red-100 text-red-800">
                      Mismatched
                    </Badge>
                  )}
                </div>
              </div>
              <CardContent className="p-3 space-y-1">
                <h4 className="text-sm font-medium truncate">{s.description}</h4>
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span className="font-mono text-primary">{s.patient_uhid}</span>
                  <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{s.study_date}</span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="flex items-center gap-1 text-muted-foreground">
                    {isFormOnlyStudy(s) && isEchoFormModality(s.modality)
                      ? "Form"
                      : (
                        <>
                          <FileText className="w-3 h-3" />
                          {s.files_count} files
                        </>
                      )}
                  </span>
                  <div className="flex items-center gap-1">
                    <button data-testid={`grid-reports-${i}`} onClick={() => setDiscussionStudy({ ...s, _initialTab: "reports" })}
                      className="p-1 hover:bg-secondary rounded" title="Reports">
                      <FilePlus2 className="w-3.5 h-3.5 text-[var(--xl-primary)]" />
                    </button>
                    <button data-testid={`grid-discuss-${i}`} onClick={() => setDiscussionStudy({ ...s, _initialTab: "discussion" })}
                      className="p-1 hover:bg-secondary rounded" title="Discussion">
                      <MessageSquare className="w-3.5 h-3.5 text-muted-foreground" />
                    </button>
                    <Badge variant={s.status === "completed" ? "default" : "outline"} className="text-[10px]">{s.status}</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-auto h-[calc(100vh-220px)]">
            <Table className="sticky-table min-w-[700px]">
              <TableHeader>
                <TableRow>
                  <TableHead>Description</TableHead>
                  <TableHead className="w-24">Patient</TableHead>
                  <TableHead className="w-24">Modality</TableHead>
                  <TableHead className="w-24">Date</TableHead>
                  <TableHead className="w-16 text-center">Files</TableHead>
                  <TableHead className="w-24">Status</TableHead>
                  <TableHead className="w-28">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">Loading...</TableCell></TableRow>
                ) : studies.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">No studies found</TableCell></TableRow>
                ) : studies.map((s, i) => {
                  const isEchoFormRow = isFormOnlyStudy(s) && isEchoFormModality(s.modality);
                  return (
                  <TableRow key={s.id} data-testid={`study-row-${i}`}>
                    <TableCell className="font-medium">{s.description}</TableCell>
                    <TableCell className="font-mono text-xs text-primary cursor-pointer" onClick={() => navigate(`/patients/${s.patient_uhid}`)}>{s.patient_uhid}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap items-center gap-1">
                        <Badge className={`text-[10px] ${getModalityBadgeClass(s.modality)}`}>
                          {getModality(s.modality)?.label ?? s.modality}
                        </Badge>
                        {isEchoFormRow && (
                          <Badge variant="outline" className="text-[10px] font-normal">Form</Badge>
                        )}
                        {s.mismatched === true && (
                          <Badge className="text-[10px] font-normal border-transparent bg-red-100 text-red-800">
                            Mismatched
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">{s.study_date}</TableCell>
                    <TableCell className="text-center">{isEchoFormRow ? "—" : s.files_count}</TableCell>
                    <TableCell><Badge variant={s.status === "completed" ? "default" : "outline"} className="text-[10px]">{s.status}</Badge></TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        {canViewStudies && (
                          <button data-testid={`view-study-${i}`} onClick={() => navigate(`/viewer/${s.id}`)} className="p-1 hover:bg-secondary rounded" title="View">
                            <Eye className="w-3.5 h-3.5 text-muted-foreground" />
                          </button>
                        )}
                        <button data-testid={`reports-study-${i}`} onClick={() => setDiscussionStudy({ ...s, _initialTab: "reports" })} className="p-1 hover:bg-secondary rounded" title="Reports">
                          <FilePlus2 className="w-3.5 h-3.5 text-[var(--xl-primary)]" />
                        </button>
                        <button data-testid={`discuss-study-${i}`} onClick={() => setDiscussionStudy({ ...s, _initialTab: "discussion" })} className="p-1 hover:bg-secondary rounded" title="Discussion">
                          <MessageSquare className="w-3.5 h-3.5 text-muted-foreground" />
                        </button>
                        {canShare && (
                          <button
                            data-testid={`share-study-${i}`}
                            onClick={() => setShareStudy(s)}
                            className="p-1 hover:bg-secondary rounded"
                            title="Share"
                          >
                            <Share2 className="w-3.5 h-3.5 text-muted-foreground" />
                          </button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      {/* Per-study Sheet — Discussion + Reports tabs */}
      <Sheet open={!!discussionStudy} onOpenChange={(open) => { if (!open) setDiscussionStudy(null); }}>
        <SheetContent className="w-[460px] sm:w-[460px] p-0 flex flex-col" data-testid="study-sheet">
          {discussionStudy && (
            <Tabs defaultValue={discussionStudy._initialTab || "discussion"} className="flex flex-col h-full">
              <div className="px-5 pt-4 pb-2 border-b border-border">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
                  {getModality(discussionStudy.modality)?.label ?? discussionStudy.modality} · {discussionStudy.patient_uhid}
                </div>
                <div className="text-sm font-medium truncate" title={discussionStudy.description}>
                  {discussionStudy.description}
                </div>
                <TabsList className="mt-3 bg-secondary/40 h-9">
                  <TabsTrigger value="discussion" className="text-xs h-7" data-testid="tab-discussion">Discussion</TabsTrigger>
                  <TabsTrigger value="reports" className="text-xs h-7" data-testid="tab-reports">Reports</TabsTrigger>
                </TabsList>
              </div>
              <TabsContent value="discussion" className="flex-1 m-0 overflow-hidden">
                <StudyDiscussion
                  studyId={discussionStudy.id}
                  studyDescription={discussionStudy.description}
                  compact
                />
              </TabsContent>
              <TabsContent value="reports" className="flex-1 m-0 overflow-hidden">
                <StudyReportsList
                  studyId={discussionStudy.id}
                  onImportClick={() => { setImportStudy(discussionStudy); }}
                />
              </TabsContent>
            </Tabs>
          )}
        </SheetContent>
      </Sheet>

      {/* Import Report Dialog */}
      <ImportReportDialog
        open={!!importStudy}
        onOpenChange={(v) => { if (!v) setImportStudy(null); }}
        studyId={importStudy?.id}
        studyDescription={importStudy?.description}
      />

      <ShareStudyDialog
        open={!!shareStudy}
        onOpenChange={(open) => { if (!open) setShareStudy(null); }}
        study={shareStudy}
      />
    </div>
  );
}
